<template>
  <div class="editor">
    <div class="block-list">
      <div id="blocksMenuWrap">
        <!-- BLOCKS MENU -->
        <BlocksMenu
          :editor="editor"
        /><!-- END BLOCKS MENU -->
      </div>
      <!-- STYLING MENU -->
      <StylingMenu
        :editor="editor"
        :keepInBounds="keepInBounds"
      /> <!-- END STYLING MENU -->
      <!-- EDITOR -->
      <EditorContent
        :editor="editor"
      /> <!-- EDITOR -->
    </div>
  </div>
</template>

<script>

import { Editor, EditorContent, BubbleMenu } from '@tiptap/vue-2'
import Bold from '@tiptap/extension-bold'
import Blockquote from '@tiptap/extension-blockquote'
import BulletList from '@tiptap/extension-bullet-list'
// import CodeBlockLowlight from '@tiptap/extension-code-block-lowlight'
import Document from '@tiptap/extension-document'
import HardBreak from '@tiptap/extension-hard-break'
import Heading from '@tiptap/extension-heading'
import HorizontalRule from '@tiptap/extension-horizontal-rule'
import History from '@tiptap/extension-history'
import Image from '@tiptap/extension-image'
import Italic from '@tiptap/extension-italic'
import Link from '@tiptap/extension-link'
import ListItem from '@tiptap/extension-list-item'
import Strike from '@tiptap/extension-strike'
import Paragraph from '@tiptap/extension-paragraph'
import Placeholder from '@tiptap/extension-placeholder'
import Text from '@tiptap/extension-text'
import Underline from '@tiptap/extension-underline'
import Youtube from '@/components/common/Editor/Blocks/Youtube'

import StylingMenu from '@/components/common/Editor/Menu/StylingMenu.vue'
import BlocksMenu from '@/components/common/Editor/Menu/MenuBlocks.vue'
// import { uploadImage } from '@/utils'

// import lowlight from 'lowlight'

// import CustomLink from '@/components/common/Editor/Blocks/CustomLink.js'
// import CustomImage from '@/components/common/Editor/Blocks/CustomImage.js'

export default {
  name: 'Editor',
  props: ['content', 'value'],
  components: {
    EditorContent,
    BlocksMenu,
    StylingMenu
  },
  data () {
    return {
      emitAfterOnUpdate: false,
      blockMenuOpen: false,
      blockSelect: false,
      keepInBounds: true,
      editor: new Editor({
        extensions: [
          Bold,
          Blockquote,
          BubbleMenu,
          BulletList,
          // CodeBlockLowlight.configure({
          //   lowlight
          // }),
          Image.configure({
            HTMLAttributes: {
              class: 'image-class'
            }
          }),
          Document,
          HardBreak,
          Heading,
          HorizontalRule,
          History,
          Italic,
          ListItem,
          Link.configure({
            HTMLAttributes: {
              target: '_blank',
              rel: 'noopener noreferrer nofollow'
            }
          }),
          Strike,
          Paragraph,
          Placeholder.configure({
            emptyEditorClass: 'is-editor-empty',
            emptyNodeClass: 'is-empty',
            placeholder: 'Escreva alguma coisa',
            showOnlyWhenEditable: true,
            showOnlyCurrent: false
          }),
          Text,
          Underline,
          Youtube
        ],
        content: this.value,
        onUpdate: ({ editor }) => {
          const content = editor.getJSON()
          this.emitAfterOnUpdate = true
          this.$emit('input', content)
        }
      })
    }
  },
  created () {
    this.setEditorContent()
  },
  watch: {
    value (newVal, oldVal) {
      if (this.emitAfterOnUpdate) {
        this.emitAfterOnUpdate = false
        return
      }
      if (newVal) this.setEditorContent()
    }
  },
  methods: {
    setEditorContent () {
      if (this.value) {
        this.editor.commands.setContent(this.value, false)
      }
    }
  },
  beforeDestroy () {
    this.editor.destroy()
  }
}
</script>

<style lang="scss" scoped>

$color-black: #000000;
$color-white: #ffffff;
$color-grey: #dddddd;

.editor {
  position: relative;
  max-width: 100%;
  margin: 0;
  position: relative;
  min-height: 200px;

  @include desktop {
    min-height: 0;
  }

  #blocksMenuWrap {
    position: absolute;
    right: 0;
    bottom: 0;
    z-index: 10;
  }

  /* EDITOR STYLE */
  ::v-deep {

    overflow-wrap: break-word;
    word-wrap: break-word;
    word-break: break-word;

    .ProseMirror {
      padding-right: 32px;

      pre{
        white-space: pre-wrap;
      }
    }

    a {
      color: $color2;
    }

    /* EMPTY PARAGRAPH */
    p.is-editor-empty::before, p.is-empty::before {
      content: attr(data-placeholder);
      float: left;
      color: #aaa;
      pointer-events: none;
      height: 0;
      font-style: italic;
    }

    /* LIST */
    ul,
    ol {
      padding-left: $spacing_large;
      li {
        list-style: disc;
      }
    }

    li > p,
    li > ol,
    li > ul {
      margin: 0;
    }

    /* HARD BREAK */
    hr {
      border-top: 1px solid rgba(0, 0, 0, 0.1) !important;
    }

    /* HEADER */
    h2, h3 {
      font-weight: bold;
    }

    /* IMAGES */
    .image-class {
      margin-bottom: 1rem;
    }
    img {
      max-width: 100%;
      width: auto;
      display: block;
      margin: 0 auto;
    }

    /* BLOCKQUOTE */
    blockquote{
      border-left: 3px solid rgba(0,0,0,.1);
      color: rgba(0,0,0,.8);
      padding-left: .8rem;
      font-style: italic;

      p{
          margin: 0;
      }
    }

    /* CODE */
    pre {
      background: #0D0D0D;
      color: #FFF;
      font-family: 'JetBrainsMono', monospace;
      padding: 0.75rem 1rem;
      border-radius: 0.5rem;

      code {
        color: inherit;
        padding: 0;
        background: none;
        font-size: 0.8rem;
      }

      .hljs-comment,
      .hljs-quote {
        color: #616161;
      }

      .hljs-variable,
      .hljs-template-variable,
      .hljs-attribute,
      .hljs-tag,
      .hljs-name,
      .hljs-regexp,
      .hljs-link,
      .hljs-name,
      .hljs-selector-id,
      .hljs-selector-class {
        color: #F98181;
      }

      .hljs-number,
      .hljs-meta,
      .hljs-built_in,
      .hljs-builtin-name,
      .hljs-literal,
      .hljs-type,
      .hljs-params {
        color: #FBBC88;
      }

      .hljs-string,
      .hljs-symbol,
      .hljs-bullet {
        color: #B9F18D;
      }

      .hljs-title,
      .hljs-section {
        color: #FAF594;
      }

      .hljs-keyword,
      .hljs-selector-tag {
        color: #70CFF8;
      }

      .hljs-emphasis {
        font-style: italic;
      }

      .hljs-strong {
        font-weight: 700;
      }
    }

    /* YOUTUBE */
    .youtube-wrapper {
      width: 100%;
      margin-bottom: 1rem;

      iframe {
        width: 100%;
        height: 160px;

        @include tablet {
          height: 222px;
        }

        @include desktop {
          height: 256px;
        }

        @include desktop-lg {
          height: 312px;
        }
      }
    }

  }
}

</style>
